import { useEffect, useState } from 'react';

const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Returns whether the user has requested reduced motion via system preferences
 * (`prefers-reduced-motion: reduce`).
 *
 * Used by CDS transition components (Fade, Slide, Collapse) to:
 * - Keep Fade at normal duration (opacity is safe for vestibular sensitivity)
 * - Fall back from Slide to Fade (spatial motion can cause discomfort)
 * - Make Collapse instant (height change is motion)
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/@media/prefers-reduced-motion
 */
export function useReducedMotion(): boolean {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }
    const mql = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);
    setReducedMotion(mql.matches);

    // Support mocks that only implement addListener/removeListener (e.g. Jest)
    if (typeof mql.addEventListener !== 'function') {
      return;
    }
    const listener = (event: MediaQueryListEvent) => {
      setReducedMotion(event.matches);
    };
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);

  return reducedMotion;
}
